import { useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MyWorkspaceTable from '../MyWorkspaceTable';
import intl from 'react-intl-universal';
import { tailwindOverride, Tooltip } from '@getsynapse/design-system';
import {
  PATHS,
  DATE,
  PROJECT_USER_ACTIONS,
  PROJECT_PERMISSIONS_BY_LEVEL,
} from 'utils/constants';
import get from 'lodash/get';
import { Project } from 'utils/customTypes';
import moment from 'utils/moment';
import {
  getStatusColumn,
  getStatusPill,
  getPriorityLabel,
} from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell';
import { useUpdateProjectStatus } from 'Pages/ProjectsListPage/hooks/useUpdateProjectStatus/useUpdateProjectStatus';
import {
  PilotPagesToTrack,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';
import { getUserAccessOnProject } from 'Pages/ProjectsListPage/helpers/getUserParticipantType';
import { selectUser, isUserOrganizationAdmin } from 'state/User/userSlice';
import { selectLearningTeams } from 'state/LearningTeams/learningTeamsSlice';

type ProjectsTableProps = {
  data: Project[];
  handleSort: (orderByParam: string[], order: 'desc' | 'asc') => void;
  cardTable: boolean;
  onOpenCancelModal?: (projectId: string) => void;
  onOpenOnHoldModal?: (projectId: string) => void;
  onOpenCloseModal?: (projectId: string) => void;
  onRefresh?: () => void;
};

type GetStatusComponentProps = {
  project: Project;
  canEditStatus: boolean;
  hasEditPermission: boolean;
  isOnlyStatusViewMode: boolean;
  valueToShow: (value: string) => JSX.Element;
  className?: string;
};

const ProjectsTable = ({
  data,
  handleSort,
  cardTable,
  onOpenCancelModal,
  onOpenOnHoldModal,
  onOpenCloseModal,
  onRefresh,
}: ProjectsTableProps) => {
  const history = useHistory();
  const { inlineEditingProjectStatusHome } = useFlags();
  const { handleSaveProjectStatus, getProjectStatuses } =
    useUpdateProjectStatus();

  const userData = useSelector(selectUser);
  const ldTeams = useSelector(selectLearningTeams);
  const isAdmin = useSelector(isUserOrganizationAdmin);

  const getStatusComponent = ({
    project,
    canEditStatus,
    hasEditPermission,
    isOnlyStatusViewMode,
    valueToShow,
    className,
  }: GetStatusComponentProps) => {
    return (
      <StatusTableRowCell
        value={project.status || ''}
        customizedValue={valueToShow}
        options={getProjectStatuses(project)}
        isEditable={!isOnlyStatusViewMode}
        className={className}
        isTaskProjectDisabled={false}
        canInlineEditTask={true}
        onSave={(newValue) => {
          handleSaveProjectStatus(
            newValue,
            canEditStatus,
            project,
            onOpenCancelModal,
            onOpenOnHoldModal,
            onOpenCloseModal,
            onRefresh
          );
        }}
        canEdit={hasEditPermission}
        entityType={StatusInlineEditingEntityType.PROJECT}
        userPilotEventOrigin={PilotPagesToTrack.HOME}
        isArchived={project.is_archived}
      />
    );
  };

  const getStatusColumnDependsOnFlag = (
    project: Project,
    canEditStatus: boolean,
    hasEditPermission: boolean,
    isOnlyStatusViewMode: boolean
  ) => {
    if (cardTable) {
      if (!inlineEditingProjectStatusHome) {
        return getStatusPill(project.status || '');
      }

      return getStatusComponent({
        project,
        canEditStatus,
        hasEditPermission,
        isOnlyStatusViewMode,
        valueToShow: getStatusPill,
        className: 'w-20',
      });
    } else {
      if (!inlineEditingProjectStatusHome) {
        return getStatusColumn(project.status || '');
      }

      return getStatusComponent({
        project,
        canEditStatus,
        hasEditPermission,
        isOnlyStatusViewMode,
        valueToShow: (status: string) => (
          <>
            {getStatusColumn(
              status as any,
              false,
              '',
              inlineEditingProjectStatusHome,
              canEditStatus
            )}
          </>
        ),
        className: 'w-32',
      });
    }
  };

  return (
    <MyWorkspaceTable
      cardTable={cardTable}
      dataCy='due-projects-table'
      headCells={[
        {
          content: intl.get('MY_WORKSPACE.DUE_PROJECTS.TABLE.HEAD_CELLS.TITLE'),
          columnName: 'title',
        },
        ...(!cardTable
          ? [
              {
                content: intl.get(
                  'MY_WORKSPACE.DUE_PROJECTS.TABLE.HEAD_CELLS.MY_TASKS'
                ),
                className: 'w-24',
                columnName: 'projectTasks.length',
              },
            ]
          : []),
        {
          content: intl.get(
            'MY_WORKSPACE.DUE_PROJECTS.TABLE.HEAD_CELLS.STATUS'
          ),
          columnName: 'status',
          className: tailwindOverride('w-28', {
            'w-20': cardTable,
            'p-0 w-32': inlineEditingProjectStatusHome && !cardTable,
          }),
        },
        {
          content: intl.get(
            'MY_WORKSPACE.DUE_PROJECTS.TABLE.HEAD_CELLS.PRIORITY'
          ),
          columnName: 'priority',
          className: 'w-28',
        },
        {
          content: intl.get(
            'MY_WORKSPACE.DUE_PROJECTS.TABLE.HEAD_CELLS.COMPLETION_DATE'
          ),
          columnName: 'targetCompletionDate',
          className: 'w-36',
        },
      ]}
      rows={data.map((project) => {
        const targetCompletionDateWithoutTime = moment(
          new Date(project.targetCompletionDate!)
        )
          .locale('en')
          .format('L');
        const currentDate = moment(new Date());
        const currentTime = currentDate.format('HH:mm:ssZ');
        const momentTargetCompletionDate = moment(
          `${targetCompletionDateWithoutTime} ${currentTime}`
        );
        let targetCompletionDateFromNow = momentTargetCompletionDate.fromNow();
        if (
          targetCompletionDateFromNow.includes('hour') ||
          targetCompletionDateFromNow.includes('minute') ||
          targetCompletionDateFromNow.includes('second')
        ) {
          targetCompletionDateFromNow = intl.get(
            'MY_WORKSPACE.DUE_TASKS.TABLE.TODAY'
          );
        }
        const targetCompletionDate = cardTable
          ? targetCompletionDateFromNow
          : momentTargetCompletionDate.format(DATE.SHORT_FORMAT);
        const dDiff = momentTargetCompletionDate.diff(moment(), 'days');
        const daysBetween =
          currentDate.date() - momentTargetCompletionDate.date();
        let targetCompletionDateStyle = '';
        if (dDiff < 0) {
          targetCompletionDateStyle = 'text-error-darker';
          if (!cardTable) {
            targetCompletionDateStyle += ' bg-error-lightest';
          }
        } else if (dDiff === 0 && daysBetween === 0) {
          targetCompletionDateStyle = 'text-warning-darker';
        }
        const targetCompletionDateComponent = (
          <span className={targetCompletionDateStyle}>
            {targetCompletionDate.charAt(0).toUpperCase() +
              targetCompletionDate.slice(1)}
          </span>
        );

        const { permissionLevel } = getUserAccessOnProject(
          project,
          userData,
          ldTeams
        );
        const availableActions = PROJECT_PERMISSIONS_BY_LEVEL[permissionLevel];
        const canUpdateProject = availableActions.includes(
          PROJECT_USER_ACTIONS.UPDATE_PROJECT
        );
        const hasEditPermission = canUpdateProject || isAdmin;
        const canEditStatus = hasEditPermission && !project.is_archived;
        const isOnlyStatusViewMode = !canEditStatus;

        return {
          id: project.id,
          className: tailwindOverride('table w-full table-fixed group', {
            'cursor-pointer': !inlineEditingProjectStatusHome,
          }),
          onClick: () =>
            !inlineEditingProjectStatusHome &&
            history.push(`${PATHS.PROJECT_PAGE}/${project.id}`),
          cells: [
            {
              content: inlineEditingProjectStatusHome ? (
                <LinkTableRowCell
                  value={project.title}
                  to={`${PATHS.PROJECT_PAGE}/${project.id}`}
                />
              ) : (
                <Link
                  to={`${PATHS.PROJECT_PAGE}/${project.id}`}
                  title={project.title}
                >
                  {project.title}
                </Link>
              ),
              className: 'truncate',
            },
            ...(!cardTable
              ? [
                  {
                    content: get(project, 'projectTasks.length'),
                    className: 'w-24 text-right',
                  },
                ]
              : []),
            {
              content: getStatusColumnDependsOnFlag(
                project,
                canEditStatus,
                hasEditPermission,
                isOnlyStatusViewMode
              ),
              className: tailwindOverride('w-28 p-0', {
                'w-20': cardTable,
                'p-0 w-32': inlineEditingProjectStatusHome && !cardTable,
              }),
              'aria-label': `due-project-status`,
            },
            {
              content: getPriorityLabel(project.priority),
              className: 'w-28',
            },
            {
              content: cardTable ? (
                targetCompletionDateComponent
              ) : (
                <Tooltip
                  trigger={targetCompletionDateComponent}
                  showPopper
                  openMode='hover1'
                  timeout={0}
                  contentProps={{ className: 'z-200000 py-1 px-3' }}
                >
                  {targetCompletionDateFromNow.charAt(0).toUpperCase() +
                    targetCompletionDateFromNow.slice(1)}
                </Tooltip>
              ),
              className: 'w-36',
            },
          ],
        };
      })}
      handleSort={handleSort}
    />
  );
};

export default ProjectsTable;
